import * as i0 from '@angular/core';
import { InjectionToken, inject, NgZone, TransferState, makeStateKey, isDevMode, provideAppInitializer, makeEnvironmentProviders, ViewContainerRef, TemplateRef, Input, Directive } from '@angular/core';
import { anonymousUser, suppressLdErrors, GenericFeatureFlagService } from '@rma/common-ui-ng/feature-flags/internal';
import { basicLogger, initialize } from 'launchdarkly-js-client-sdk';
import { ReplaySubject, fromEventPattern, of, tap, map } from 'rxjs';
import { startWith, switchMap, take } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { WA_WINDOW } from '@ng-web-apis/common';
const LAUNCH_DARKLY_CLIENT_ID = new InjectionToken('LaunchDarkly.ClientId');
class FeatureFlagsBrowserService {
  clientId = inject(LAUNCH_DARKLY_CLIENT_ID);
  zone = inject(NgZone);
  state = inject(TransferState, {
    optional: true
  });
  featureSetKey = makeStateKey('ld_flags');
  ldClient;
  isIdentified = false;
  featureWatchers = {};
  retake$ = new ReplaySubject(1);
  load() {
    return new Promise((resolve, _reject) => this.zone.runOutsideAngular(() => this.initialiseClient(resolve)));
  }
  getFeature(key, defaultValue) {
    return this.ldClient?.variation(key, defaultValue) ?? defaultValue;
  }
  getFeature$(key, defaultValue) {
    return this.retake$.pipe(startWith(null),
    // trigger initial fetch
    switchMap(() => this.getLiveFeature$(key, defaultValue).pipe(take(1))));
  }
  getLiveFeature$(key, defaultValue) {
    if (this.featureWatchers[key]) {
      return this.featureWatchers[key];
    }
    const initialValue = this.ldClient?.variation(key, defaultValue) ?? defaultValue;
    const feature$ = this.ldClient ? fromEventPattern(handler => this.ldClient?.on?.(`change:${key}`, handler)).pipe(startWith(initialValue)) : of(initialValue);
    this.featureWatchers[key] = feature$;
    return feature$;
  }
  async identify(user) {
    if (this.ldClient) {
      await this.ldClient.identify(user);
      this.isIdentified = true;
    }
  }
  async unidentify() {
    if (this.isIdentified && this.ldClient) {
      await this.ldClient.identify(anonymousUser);
      this.isIdentified = false;
    }
  }
  forceRefresh() {
    this.retake$.next();
  }
  initialiseClient(resolve) {
    const flags = this.state?.get(this.featureSetKey, undefined);
    const bootstrapConfig = flags && Object.values(flags).length ? {
      bootstrap: flags
    } : {};
    const config = {
      ...bootstrapConfig,
      logger: basicLogger({
        level: isDevMode() ? 'warn' : 'error'
      }),
      sendEvents: true
    };
    this.ldClient = initialize(this.clientId, anonymousUser, config);
    this.ldClient.on('ready', () => resolve());
    this.ldClient.on('error', suppressLdErrors);
  }
}
function initialiseLaunchDarkly(launchDarklyService) {
  return () => launchDarklyService.load();
}
function initialiseUserIdentification(featureUserService) {
  return () => featureUserService.initialise();
}
function provideRmaFeatureFlagsBrowser(clientId, userIdToken) {
  const providers = [{
    provide: LAUNCH_DARKLY_CLIENT_ID,
    useValue: clientId
  }, {
    provide: GenericFeatureFlagService,
    useClass: FeatureFlagsBrowserService
  }, provideAppInitializer(() => {
    const initializerFn = initialiseLaunchDarkly(inject(GenericFeatureFlagService));
    return initializerFn();
  })];
  if (userIdToken) {
    providers.push(provideAppInitializer(() => {
      const initializerFn = initialiseUserIdentification(inject(userIdToken));
      return initializerFn();
    }));
  }
  return makeEnvironmentProviders(providers);
}
let GenericIfFeatureFlagDirective = /*#__PURE__*/(() => {
  class GenericIfFeatureFlagDirective {
    rmaIfFeatureFlag;
    vcRef = inject(ViewContainerRef);
    tplRef = inject(TemplateRef);
    featureFlagService = inject(GenericFeatureFlagService);
    ngOnInit() {
      if (this.rmaIfFeatureFlag && this.featureFlagService.getFeature(this.rmaIfFeatureFlag, false)) {
        this.vcRef.createEmbeddedView(this.tplRef);
      }
    }
    static ɵfac = function GenericIfFeatureFlagDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GenericIfFeatureFlagDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: GenericIfFeatureFlagDirective,
      selectors: [["", "rmaIfFeatureFlag", ""]],
      inputs: {
        rmaIfFeatureFlag: "rmaIfFeatureFlag"
      }
    });
  }
  return GenericIfFeatureFlagDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use when a route should be conditionally blocked based on a feature flag.
 * Redirects to the specified page if it evaluates false.
 * Use the type parameter if you need to use a non-boolean flag.
 * @param key Feature flag key
 * @param defaultValue A fallback value in case the flag cannot be retrieved
 * @param options (Optional) Various options to configure behaviour. See the type's documentation for more detail.
 * @returns True if permitted, false with redirect if not permitted
 */
function featureFlagGuard(key, defaultValue, options) {
  return () => {
    const window = inject(WA_WINDOW, {
      optional: true
    });
    const document = inject(DOCUMENT);
    const router = inject(Router);
    const flagPredicate = options?.flagPredicate ?? (x => Boolean(x));
    const redirectTo = options?.redirectTo ?? '/';
    const logAsError = options?.logAsError !== undefined ? options.logAsError : true;
    return getFeature(key, defaultValue, flagPredicate).pipe(tap(({
      flagValue,
      permitted
    }) => {
      if (!permitted) {
        if (logAsError) {
          console.error('FEATURE FLAG GUARD | redirected', {
            flagKey: key,
            flagValue
          }, {
            location: window?.location.href
          }, {
            referrer: document.referrer
          }, {
            redirectTo
          });
        }
        router.navigate([redirectTo], options?.queryParams);
      }
    }), map(({
      permitted
    }) => permitted));
  };
}
function featureFlagCanMatch(key, defaultValue, flagPredicate) {
  return () => {
    const predicate = flagPredicate ?? (x => Boolean(x));
    return getFeature(key, defaultValue, predicate).pipe(map(({
      permitted
    }) => permitted));
  };
}
function getFeature(key, defaultValue, flagPredicate) {
  const featureService = inject(GenericFeatureFlagService);
  return featureService.getFeature$(key, defaultValue).pipe(map(x => ({
    flagValue: x,
    permitted: flagPredicate(x)
  })));
}

/**
 * Generated bundle index. Do not edit.
 */

export { FeatureFlagsBrowserService, GenericIfFeatureFlagDirective, featureFlagCanMatch, featureFlagGuard, provideRmaFeatureFlagsBrowser };
